import React, { useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiLayers, FiSearch, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import Layout from '../components/Layout';
import API from '../api/axios';
import { toast } from 'react-toastify';
import './Projects.css';
import './Modules.css';

const emptyForm = { name: '', key: '', description: '', isActive: true };

const MODULE_ICONS = ['📊','📁','👥','📦','🏠','📋','✅','🏗️','🛒','💰','📝','🔍','📅','🌿','🛡️','⚙️'];
const modIcon = (name) => MODULE_ICONS[name?.charCodeAt(0) % MODULE_ICONS.length] || '📦';

export default function Modules() {
  const [modules, setModules] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');

  const fetchModules = () => API.get('/modules').then(r => setModules(r.data)).catch(() => {});

  useEffect(() => { fetchModules(); }, []);

  const openModal = (mod = null) => {
    setEditing(mod);
    setForm(mod ? { name: mod.name, key: mod.key, description: mod.description || '', isActive: mod.isActive } : emptyForm);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await API.put(`/modules/${editing._id}`, form);
        toast.success('Module updated');
      } else {
        await API.post('/modules', form);
        toast.success('Module created');
      }
      setShowModal(false);
      fetchModules();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving module');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this module?')) return;
    try {
      await API.delete(`/modules/${id}`);
      toast.success('Module deleted');
      fetchModules();
    } catch {
      toast.error('Error deleting module');
    }
  };

  const filtered = modules.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.key?.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = modules.filter(m => m.isActive).length;

  return (
    <Layout>
      <div className="page">
        <div className="page-actions">
          <button className="btn-primary-sm" onClick={() => openModal()}>
            <FiPlus /> New Module
          </button>
        </div>

        {/* Stats */}
        <div className="module-stats">
          <div className="module-stat-card">
            <div className="module-stat-icon" style={{ background: '#eff6ff', color: '#3b82f6' }}><FiLayers /></div>
            <div>
              <p className="user-stat-num">{modules.length}</p>
              <p className="user-stat-label">Total Modules</p>
            </div>
          </div>
          <div className="module-stat-card">
            <div className="module-stat-icon" style={{ background: '#f0fdf4', color: '#10b981' }}><FiToggleRight /></div>
            <div>
              <p className="user-stat-num">{activeCount}</p>
              <p className="user-stat-label">Active</p>
            </div>
          </div>
          <div className="module-stat-card">
            <div className="module-stat-icon" style={{ background: '#fef2f2', color: '#ef4444' }}><FiToggleLeft /></div>
            <div>
              <p className="user-stat-num">{modules.length - activeCount}</p>
              <p className="user-stat-label">Inactive</p>
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="table-container">
          <div className="table-toolbar">
            <div className="search-box">
              <FiSearch />
              <input placeholder="Search modules..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <span className="result-count">{filtered.length} module{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          <table>
            <thead>
              <tr>
                <th>Module</th>
                <th>Key</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(mod => (
                <tr key={mod._id}>
                  <td>
                    <div className="module-cell">
                      <div className="module-emoji">{modIcon(mod.name)}</div>
                      <span className="module-cell-name">{mod.name}</span>
                    </div>
                  </td>
                  <td>
                    <code className="module-key-badge">{mod.key}</code>
                  </td>
                  <td className="text-muted">{mod.description || '—'}</td>
                  <td>
                    <span className="status-dot-badge" style={mod.isActive
                      ? { background: '#f0fdf4', color: '#10b981' }
                      : { background: '#fef2f2', color: '#ef4444' }}>
                      <span className="dot" style={{ background: mod.isActive ? '#10b981' : '#ef4444' }} />
                      {mod.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="td-actions">
                      <button className="action-btn edit" onClick={() => openModal(mod)} title="Edit"><FiEdit2 /></button>
                      <button className="action-btn delete" onClick={() => handleDelete(mod._id)} title="Delete"><FiTrash2 /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="empty-msg">
                  {search ? `No modules matching "${search}"` : 'No modules yet.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editing ? 'Edit Module' : 'New Module'}</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Module Name</label>
                    <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Projects" />
                  </div>
                  <div className="form-group">
                    <label>Key</label>
                    <input value={form.key} onChange={e => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })} required placeholder="e.g. projects" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional description" />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.value === 'true' })}>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">{editing ? 'Update Module' : 'Create Module'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
